import { AssignCard } from './../data/AssignCard';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { ProfileSearchResult } from '../data/ProfileSearchResult';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { HeaderService } from './header.service';
import { environment } from '../../environments/environment';
import { CardholderDetails } from '../data/CardholderDetails';
import { CustomerAccount } from '../data/CustomerAccount';
import { TransactionInfo } from '../data/TransactionInfo';
import { Transaction } from '../data/Transaction';
import { ACHFundingInfo } from '../data/ACHFundingInfo';
import { AchRequest } from '../data/AchRequest';
import { CardholderPersonalInfo } from '../data/CardholderPersonalInfo';
import { Note } from '../Note';
import { NoteCategory } from '../data/NoteCategory';

@Injectable({
  providedIn: 'root'
})
export class CardholderService {
  @Output() sendChipDetails: EventEmitter<boolean> = new EventEmitter();
  @Output() checkForClosedLoop: EventEmitter<boolean> = new EventEmitter();
  static readonly CARDHOLDER_SEARCH_STRING = 'cardholderSearch';
  static readonly CARDHOLDER_STRING = 'cardholder';

  newSearchLocation = '';
  newSearchCardholderName = '';
  hasPendingSearch = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private headerService: HeaderService
  ) {}

  chipDetails(chp: boolean) {
    this.sendChipDetails.emit(chp);
  }

  isClosedLoopCard(clsdLoop: boolean) {
    this.checkForClosedLoop.emit(clsdLoop);
  }

  static retrieveAllCardholderResults(): ProfileSearchResult[] {
    const jsonString = sessionStorage.getItem(
      CardholderService.CARDHOLDER_SEARCH_STRING
    );
    if (jsonString === null || jsonString.trim().length === 0) {
      return new Array<ProfileSearchResult>();
    }

    return JSON.parse(jsonString) as ProfileSearchResult[];
  }

  static storeCardHolder(cardholder: ProfileSearchResult) {
    sessionStorage.setItem(
      CardholderService.CARDHOLDER_STRING,
      JSON.stringify(cardholder)
    );
  }

  static storeAllCardholderResults(cardHolderArray: ProfileSearchResult[]) {
    sessionStorage.setItem(
      CardholderService.CARDHOLDER_SEARCH_STRING,
      JSON.stringify(cardHolderArray)
    );
  }

  static retrieveCardholder(): ProfileSearchResult {
    const jsonString = sessionStorage.getItem(
      CardholderService.CARDHOLDER_STRING
    );
    return JSON.parse(jsonString) as ProfileSearchResult;
  }

  searchCardHolders(
    firstParameter: string,
    shipLocation: string
  ): Observable<ProfileSearchResult[]> {
    const url = `${environment.rootApiUrl}/cardholders/search`;
    let params = new HttpParams();
    params = params.append('firstParameter', firstParameter || '');
    params = params.append('shipUIC', shipLocation || '');

    return this.http.post<ProfileSearchResult[]>(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie()
    });
  }

  getCardholdersByPayrollAccount(payrollAccount: string): Observable<ProfileSearchResult[]> {
    const url =
      `${environment.rootApiUrl}/cardholders/searchUserByPayroll/` + payrollAccount;
    return this.http.get<ProfileSearchResult[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getCardholderDetails(payrolAccount: number): Observable<CardholderDetails> {
    const url = `${environment.rootApiUrl}/cardholders/profile/` + payrolAccount.toString();
    return this.http.get<CardholderDetails>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getCardDetails(cardSeq: number): Observable<string> {
    const url = `${environment.rootApiUrl}/common/visaForward/cardDetails/` + cardSeq.toString();
    return this.http.get<string>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  assignCard(assignCard: AssignCard): Observable<string> {
    const url = `${environment.rootApiUrl}/cardholders/profile/assignCard`;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, assignCard, {
      headers: headers,
      responseType: 'text'
    });
  }

  getMaxFormSeqForIdentity(idSeq: number): Observable<number> {
    const url = `${environment.rootApiUrl}/cardholders/profile/formSeq/` + idSeq.toString();
    return this.http.get<number>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  saveStripAccount(
    payrollAccount: number,
    stripAccount: CustomerAccount
  ): Observable<string> {
    const url =
      `${environment.rootApiUrl}/cardholders/profile/updateStripAccount/` +
      payrollAccount.toString();
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, stripAccount, {
      headers: headers,
      responseType: 'text'
    });
  }

  saveChipCard(payrollAccount: number, chipCard: any): Observable<string> {
    const url =
      `${environment.rootApiUrl}/cardholders/profile/updateChipCard/` +
      payrollAccount.toString();
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, chipCard, {
      headers: headers,
      responseType: 'text'
    });
  }

  getC2CDetails(
    payrollAccount: number,
    tranSeq: number,
    tranType: string
  ): Observable<Transaction> {
    const url = `${environment.rootApiUrl}/common/cardholders/c2cDetails/`;
    let params = new HttpParams();
    params = params.append('acctNum', payrollAccount.toString() || '');
    params = params.append('tranSeq', tranSeq.toString() || '');
    params = params.append('tranType', tranType || '');

    return this.http.get<Transaction>(url, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      params: params
    });
  }

  getTransHistory(
    payrollAccount: number,
    chipAccount: number,
    startDate: string,
    endDate: string
  ): Observable<TransactionInfo> {
    const url =
      `${environment.rootApiUrl}/common/cardholders/transactions/` +
      payrollAccount.toString() +
      '/' +
      chipAccount.toString();
    let params = new HttpParams();
    params = params.append('startDate', startDate || '');
    params = params.append('endDate', endDate || '');

    return this.http.post<TransactionInfo>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  reverse(tranSeq: number) {
    const url = `${environment.rootApiUrl}/common/reverse`;
    let params = new HttpParams();
    params = params.append('tranSeq', tranSeq.toString() || '');

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  getAchfundingInfo(payrollAccount: number): Observable<ACHFundingInfo> {
    const url = `${environment.rootApiUrl}/cardholders/achfunding`;
    let params = new HttpParams();
    params = params.append('acctNum', payrollAccount.toString() || '');

    return this.http.get<ACHFundingInfo>(url, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      params: params
    });
  }

  showAchRequestHistory(
    payrollAccount: number,
    startDateString: string,
    endDateString: string
  ): Observable<AchRequest[]> {
    const url = `${environment.rootApiUrl}/common/achfunding/history`;
    let params = new HttpParams();
    params = params.append('payrollAccount', payrollAccount.toString() || '');
    params = params.append('startMonth', startDateString.toString() || '');
    params = params.append('endMonth', endDateString.toString() || '');

    return this.http.post<AchRequest[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  checkDuplicateACHAccount(
    payrollAccount: number,
    achFundinInfo: ACHFundingInfo
  ) {
    const url =
      `${environment.rootApiUrl}/cardholders/achfunding/chkDupAchAct/` +
      payrollAccount.toString();
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, achFundinInfo, {
      headers: headers,
      responseType: 'text'
    });
  }

  updateACHFundingInfo(payrollAccount: number, achFundinInfo: ACHFundingInfo) {
    const url =
      `${environment.rootApiUrl}/cardholders/achfunding/update/` +
      payrollAccount.toString();
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, achFundinInfo, {
      headers: headers,
      responseType: 'text'
    });
  }

  loadAdjustments() {
    const url = `${environment.rootApiUrl}/common/adjustments/read`;
    return this.http.get(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getAdjustmentTransHistory(
    startDate: string,
    endDate: string,
    account: number,
    type: string
  ) {
    const url = `${environment.rootApiUrl}/common/cardholders/adjustmentTransactions/${account}/${type}`;
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);

    return this.http.post(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  applyAdjustment(accountNum: number, adjustReason: string, adjustAmt: string) {
    const url = `${environment.rootApiUrl}/common/adjustments/balance_adj`;

    let params = new HttpParams();
    params = params.append('acctNum', accountNum.toString());
    params = params.append('adjustReason', adjustReason);
    params = params.append('adjustAmt', adjustAmt);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  balanceTransfer(srcAcct: string, desAcct: string, xferAmt: string) {
    const url = `${environment.rootApiUrl}/common/adjustments/balance_xfer`;

    let params = new HttpParams();
    params = params.append('srcAcct', srcAcct);
    params = params.append('desAcct', desAcct);
    params = params.append('xferAmt', xferAmt);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  getCardholdersPersonalInfo(
    payrollAccount: number
  ): Observable<CardholderPersonalInfo> {
    const url =
      `${environment.rootApiUrl}/cardholders/profile/personalinfo/` +
      payrollAccount.toString();

    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.get<CardholderPersonalInfo>(url, {headers: headers});
  }

  saveLocation(
    payrollAccount: number,
    location: string,
    meu: string,
    idSeq: number
  ): Observable<string> {
    const url = `${environment.rootApiUrl}/cardholders/update/shiplocation`;
    let parms = new HttpParams();
    parms = parms.append('acctNum', payrollAccount.toString());
    parms = parms.append('location', location);
    parms = parms.append('meu', meu);
    parms = parms.append('idSeq', idSeq.toString());

    return this.http.post(url, parms, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  savePersonal(payrollAccount: number, personalInfo: CardholderPersonalInfo) {
    const url =
      `${environment.rootApiUrl}/cardholders/update/personalinfo/` +
      payrollAccount.toString();
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, personalInfo, {
      headers: headers,
      responseType: 'text'
    });
  }

  getNotes(payrollAccount: number): Observable<Note[]> {
    const url =
      `${environment.rootApiUrl}/common/notes/` + payrollAccount.toString();

    return this.http.get<Note[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getNoteCategory(): Observable<NoteCategory[]> {
    const url = `${environment.rootApiUrl}/common/notes/notecategory`;

    return this.http.get<NoteCategory[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  saveNote(
    payrollAccount: number,
    noteCat: string,
    noteType: string,
    noteCase: string,
    noteDetail: string
  ): Observable<Note[]> {
    const url = `${environment.rootApiUrl}/common/notes/sendnote`;
    const params = new HttpParams()
      .append('payrollAccount', payrollAccount.toString())
      .append('note_cat', noteCat)
      .append('note_type', noteType)
      .append('note_case', noteCase)
      .append('note_detail', noteDetail);

    return this.http.post<Note[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  downloadEnrollmentPDF(
    payrollAccount: number,
    formsNumber: number,
    fileName: string
  ) {
    const url = `${environment.rootApiUrl}/cardholders/profile/pdf/enrollment/download`;
    const params = new HttpParams()
      .append('accountNumber', payrollAccount.toString())
      .append('formsNumber', formsNumber.toString())
      .append('fileName', fileName);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'arraybuffer'
    });
  }

  getMerchantForms(accountNumber: string) {
    const url = `${environment.rootApiUrl}/cardholders/profile/pdf/merchant/downloads?accountNumber=${accountNumber}`;

    return this.http.get(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  storeCardholderResult(cardholder: ProfileSearchResult) {
    // Get the list of ships
    const cardholderArray = CardholderService.retrieveAllCardholderResults();
    let uniqueEntry = true;

    cardholderArray.forEach((cardholderResult: ProfileSearchResult) => {
      if (cardholderResult.payrollAccount === cardholder.payrollAccount) {
        uniqueEntry = false;
      }
    });

    if (uniqueEntry) {
      const newArray = cardholderArray.slice(0).splice(-4);
      newArray.push(cardholder);

      // Store the new array
      CardholderService.storeAllCardholderResults(newArray);
    }
  }

  getPendingSearch(): [string, string] | null {
    if (this.hasPendingSearch) {
      this.hasPendingSearch = false;
      return [this.newSearchLocation, this.newSearchCardholderName];
    } else {
      return null;
    }
  }

  setPendingSearch(locationString: string, cardholderNameString) {
    this.newSearchLocation = locationString;
    this.newSearchCardholderName = cardholderNameString;
    this.hasPendingSearch = true;
  }

  uploadForm(acctNum: string, file: File) {
    const url = `${environment.rootApiUrl}/cardholders/formsupload/enr`;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('acctNum', acctNum);
    const headers = this.headerService.getXAuthHeaderFromCookieNoType();

    return this.http.post(url, fd, { headers: headers, responseType: 'text' });
  }

}
